import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Filter, Search as SearchIcon, MapPin, Star, X } from "lucide-react";
import api from "../lib/api";
import ProfileCard from "../components/ProfileCard";
import useSEO from "../hooks/useSEO";

const CONFIG = {
  professionnels: { type: "professional", title: "Annuaire des professionnels", subtitle: "Trouvez le professionnel francophone qui répondra à vos besoins." },
  organismes: { type: "organism", title: "Répertoire des organismes", subtitle: "Organismes communautaires et d'accueil pour vous accompagner." },
  entrepreneurs: { type: "entrepreneur", title: "Entrepreneurs immigrants", subtitle: "Soutenez les entreprises locales issues de la diaspora." },
};

const PROVINCES = ["Alberta", "Colombie-Britannique", "Île-du-Prince-Édouard", "Manitoba", "Nouveau-Brunswick", "Nouvelle-Écosse", "Nunavut", "Ontario", "Québec", "Saskatchewan", "Terre-Neuve-et-Labrador", "Territoires du Nord-Ouest", "Yukon"];
const RATINGS = [
  { value: 0, label: "Toutes" },
  { value: 4, label: "4★ et plus" },
  { value: 4.5, label: "4.5★ et plus" },
];

/** Small SVG for the Québec fleur-de-lys — used to visually mark the "Ici Québec" filter. */
function FleurDeLys({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12 2c-.9 0-1.7.9-1.7 2 0 .8.3 1.5.7 1.9-1.4.9-2 2.4-2 4.1 0 1.2.4 2.3 1 3.2H8v2h2v1H8v2h2v3.8l-1.5.5-.5 1 4 .5 4-.5-.5-1-1.5-.5V18h2v-2h-2v-1h2v-2h-2c.6-.9 1-2 1-3.2 0-1.7-.6-3.2-2-4.1.4-.4.7-1.1.7-1.9 0-1.1-.8-2-1.7-2zm-4 8c-1 0-2 .3-2.8.9C4.4 11.5 4 12.4 4 13.3c0 1.3.9 2.4 2 2.7 0-2.4 1-4.5 2-6zm8 0c1 1.5 2 3.6 2 6 1.1-.3 2-1.4 2-2.7 0-.9-.4-1.8-1.2-2.4-.8-.6-1.8-.9-2.8-.9z"/>
    </svg>
  );
}

export default function Annuaire() {
  const { kind } = useParams();
  const config = CONFIG[kind] || CONFIG.professionnels;
  useSEO({ title: config.title, description: config.subtitle });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => ({
    search: params.get("q") || "",
    category: params.get("category") || "",
    province: params.get("province") || "",
    city: params.get("city") || "",
    minRating: parseFloat(params.get("rating") || "0"),
  }), [params]);

  const setFilter = (key, val) => {
    const p = new URLSearchParams(params);
    if (val === "" || val === null || val === undefined || val === 0 || val === "0") p.delete(mapKey(key));
    else p.set(mapKey(key), String(val));
    setParams(p, { replace: true });
  };
  const mapKey = (k) => ({ search: "q", category: "category", province: "province", city: "city", minRating: "rating" }[k]);

  const resetAll = () => setParams(new URLSearchParams(), { replace: true });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = { profile_type: config.type, limit: 200 };
    if (filters.search) q.search = filters.search;
    if (filters.category) q.category = filters.category;
    if (filters.province) q.province = filters.province;
    if (filters.city) q.city = filters.city;
    if (filters.minRating > 0) q.min_rating = filters.minRating;
    api.get("/profiles", { params: q })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [kind, config.type, filters]);

  const activeCount = [filters.search, filters.category, filters.province, filters.city, filters.minRating > 0 && "r"].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`annuaire-${kind}-page`}>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight">{config.title}</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">{config.subtitle}</p>

      {/* Quick filter chips */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("province", filters.province === "Québec" ? "" : "Québec")}
          data-testid="filter-ici-quebec"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${
            filters.province === "Québec"
              ? "bg-primary text-primary-foreground border-primary shadow-sm"
              : "bg-white border-primary/20 text-primary hover:bg-secondary"
          }`}
        >
          <FleurDeLys className="w-4 h-4" />
          Ici Québec
        </button>
        {activeCount > 0 && (
          <button onClick={resetAll} data-testid="filters-reset" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" /> Réinitialiser les filtres ({activeCount})
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8 mt-8">
        {/* Sidebar filters */}
        <aside className="card-surface p-5 h-fit lg:sticky lg:top-24 space-y-5" data-testid="annuaire-filters">
          <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
            <Filter className="w-4 h-4 text-primary" />
            <h2 className="font-display font-semibold text-sm uppercase tracking-wider">Filtres</h2>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Recherche par nom</label>
            <div className="relative mt-1.5">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={filters.search}
                onChange={(e) => setFilter("search", e.target.value)}
                data-testid="filter-search"
                placeholder="Nom, mot-clé…"
                className="w-full pl-9 pr-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Catégorie / secteur</label>
            <select
              value={filters.category}
              onChange={(e) => setFilter("category", e.target.value)}
              data-testid="filter-category"
              className="w-full mt-1.5 px-3 py-2 border border-primary/15 rounded-lg text-sm bg-white outline-none focus:border-primary"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Province</label>
            <select
              value={filters.province}
              onChange={(e) => setFilter("province", e.target.value)}
              data-testid="filter-province"
              className="w-full mt-1.5 px-3 py-2 border border-primary/15 rounded-lg text-sm bg-white outline-none focus:border-primary"
            >
              <option value="">Toutes les provinces</option>
              {PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Ville</label>
            <div className="relative mt-1.5">
              <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={filters.city}
                onChange={(e) => setFilter("city", e.target.value)}
                data-testid="filter-city"
                placeholder="ex. Québec, Montréal…"
                className="w-full pl-9 pr-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Note minimale</label>
            <div className="mt-2 space-y-1.5">
              {RATINGS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rating"
                    checked={filters.minRating === r.value}
                    onChange={() => setFilter("minRating", r.value)}
                    data-testid={`filter-rating-${r.value}`}
                    className="accent-primary"
                  />
                  <span className="text-sm inline-flex items-center gap-1">
                    {r.value > 0 && <Star className="w-3.5 h-3.5 text-accent fill-accent" />}
                    {r.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        {/* Results grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground" data-testid="annuaire-results-count">
              {loading ? "Chargement…" : `${items.length} résultat${items.length > 1 ? "s" : ""}`}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {items.map((p) => <ProfileCard key={p.id} profile={p} />)}
          </div>
          {!loading && items.length === 0 && (
            <div className="text-center py-20 text-muted-foreground border border-dashed border-primary/20 rounded-2xl" data-testid="annuaire-empty">
              Aucune fiche ne correspond à vos critères.
              <br />
              <button onClick={resetAll} className="text-primary hover:underline mt-3 text-sm inline-block">Réinitialiser les filtres →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
